import $ from 'jquery';

// The expression grammar behind the commission expression editor (expression-builder-v2.js).
// Pure and DOM-free so it can be unit tested: given the expression text, the function
// catalogue and the variable list, it builds the tree, validates it (by evaluating it with
// the stub functions) and renders the canonical form that the editor SAVES
// (getExpressionTree().toString(true) — fully parenthesised, variables as [variableId]).
//
// Originally extracted from the jQuery plugin (author: Majid Akbari). The grammar has since been
// aligned with the server's (Flee): tiered left-to-right precedence and a proper unary minus.
// expression-parser.test.js and the shared corpus (Tests/Fixtures/commissionExpressionCorpus.json)
// pin both the canonical output and the validation messages.

//Author: Majid Akbari
var __extends = (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();

export function createExpressionParser(expression, options) {
    var defaults = {
        funcs: {},
        variables: []
    };
    options = $.extend({}, defaults, options);
    //abstract base-class
    var GraphNode = /** @class */ (function () {
        function GraphNode() {
        }
        return GraphNode;
    }());
    //leaf-nodes
    var ValueNode = /** @class */ (function (_super) {
        __extends(ValueNode, _super);
        function ValueNode(value) {
            var _this = _super.call(this) || this;
            _this.value = value;
            return _this;
        }
        ValueNode.prototype.getNumberValue = function () {
            return parseFloat(this.value.toString());
        };
        ValueNode.prototype.getStringValue = function () {
            return this.value.toString();
        };
        ValueNode.prototype.getArrayValue = function () {
            return this.value;
        };
        ValueNode.prototype.compute = function () { return this.value; };
        ValueNode.prototype.toString = function (parseVariables) {
            if (parseVariables === void 0) { parseVariables = false; }
            if (this.value instanceof Array)
                return this.value
                    .filter(function (v) { return !(v instanceof CommaNode); })
                    .map(function (v) { return v.toString(true); })
                    .join(",");
            if (typeof this.value === 'string')
                return "\"" + this.value + "\"";
            return this.value.toString();
        };
        return ValueNode;
    }(GraphNode));
    var CommaNode = /** @class */ (function (_super) {
        __extends(CommaNode, _super);
        function CommaNode() {
            return _super !== null && _super.apply(this, arguments) || this;
        }
        CommaNode.prototype.compute = function () {
            return ",";
        };
        CommaNode.prototype.toString = function (parseVariables) {
            return ",";
        };
        return CommaNode;
    }(GraphNode));
    var PropertyNode = /** @class */ (function (_super) {
        __extends(PropertyNode, _super);
        function PropertyNode(property, inBrackets) {
            if (inBrackets === void 0) { inBrackets = false; }
            var _this = _super.call(this) || this;
            _this.property = property;
            _this.inBrackets = inBrackets;
            return _this;
        }
        PropertyNode.prototype.compute = function () {
            var variable;
            for (var i = 0; i < options.variables.length; i++) {
                var v = options.variables[i];
                if (v.name == this.property) {
                    variable = v;
                    break;
                }
            }
            if (variable === undefined) {
                throw new Error("Property '" + this.property + "' is not defined!");
            }
            return variable.value || variable.variableId;
        };
        PropertyNode.prototype.toString = function (parseVariables) {
            if (parseVariables === void 0) { parseVariables = false; }
            if (parseVariables)
                for (var i = 0; i < options.variables.length; i++) {
                    var v = options.variables[i];
                    if (v.name == this.property)
                        return "[" + v.variableId + "]";
                }
            return String(this.property);
        };
        return PropertyNode;
    }(GraphNode));
    //tree-nodes
    var FuncNode = /** @class */ (function (_super) {
        __extends(FuncNode, _super);
        function FuncNode(name, node) {
            var _this = this;
            if (!(node instanceof GraphNode)) {
                throw new Error("invalid node passed");
            }
            _this = _super.call(this) || this;
            _this.name = name;
            _this.node = node;
            return _this;
        }
        FuncNode.prototype.compute = function () {
            var v = this.node.compute();
            var vars = v instanceof Array ? v : [v];
            var computes = vars
                .filter(function (v) { return v != ","; }) //remove ,
                .map(function (v) { return v instanceof GraphNode ? v.compute() : v; }); //compute each one
            var func = options.funcs[this.name];
            return func.apply(func, computes);
        };
        FuncNode.prototype.toString = function (parseVariables) {
            if (parseVariables === void 0) { parseVariables = false; }
            return this.name + "(" + this.node.toString(parseVariables) + ")";
        };
        return FuncNode;
    }(GraphNode));
    var BinaryNode = /** @class */ (function (_super) {
        __extends(BinaryNode, _super);
        function BinaryNode(op, left, right) {
            var _this = this;
            if (!(left instanceof GraphNode && right instanceof GraphNode)) {
                throw new Error("invalid node passed");
            }
            _this = _super.call(this) || this;
            _this.op = op;
            _this.left = left;
            _this.right = right;
            return _this;
        }
        BinaryNode.prototype.compute = function () {
            var l = this.left.compute();
            var r = this.right.compute();
            switch (this.op) {
                //computational operators
                case "+": return l + r;
                case "-": return l - r;
                case "*": return l * r;
                case "/": return l / r;
                case "%": return l % r;
                case "=": return l == r;
                case "<>": return l != r;
                case "|": return l || r;
                case "&": return l && r;
                case "XOR": return Boolean(l) !== Boolean(r);
                case ">": return l > r;
                case "<": return l < r;
                case ">=": return l >= r;
                case "<=": return l <= r;
            }
            throw new Error("operator not implemented '" + this.op + "'");
        };
        BinaryNode.prototype.toString = function (parseVariables) {
            if (parseVariables === void 0) { parseVariables = false; }
            var op = BinaryNode.isKeyword(this.op) ? " " + this.op + " " : this.op;
            return "(" + this.left.toString(parseVariables) + op + this.right.toString(parseVariables) + ")";
        };
        BinaryNode.isKeyword = function (op) {
            return /^[A-Z]+$/.test(op);
        };
        // Every symbolic binary operator the tokenizer recognises. Two-character operators
        // come first so "<>" is not read as "<" ">". The keyword operators (AND OR NOT XOR) are
        // matched separately, case-insensitively and on word boundaries, and normalised in
        // extractTokens: AND -> "&" and OR -> "|" so saved expressions keep the symbolic form
        // hand-authored ones have always had (the server maps & and | back to AND and OR).
        BinaryNode.operators = ["<>", "<=", ">=", "*", "/", "%", "+", "-", "=", "|", "&", ">", "<"];
        BinaryNode.keywords = { AND: "&", OR: "|", NOT: "NOT", XOR: "XOR" };
        // Precedence, tightest first; binary tiers reduce left to right, prefix operators right
        // to left. Mirrors Flee (the server grammar behind CommissionValueEval) so the editor
        // parenthesises the way the server evaluates: NOT binds looser than a comparison and
        // XOR is the loosest operator of all.
        BinaryNode.tiers = [
            { prefix: "-" },
            { binary: ["*", "/", "%"] },
            { binary: ["+", "-"] },
            { binary: ["=", "<>", "<", ">", "<=", ">="] },
            { prefix: "NOT" },
            { binary: ["&"] },
            { binary: ["|"] },
            { binary: ["XOR"] }
        ];
        return BinaryNode;
    }(GraphNode));
    // prefix operators: unary minus and NOT
    var UnaryNode = /** @class */ (function (_super) {
        __extends(UnaryNode, _super);
        function UnaryNode(op, node) {
            var _this = this;
            if (!(node instanceof GraphNode)) {
                throw new Error("invalid node passed");
            }
            _this = _super.call(this) || this;
            _this.op = op;
            _this.node = node;
            return _this;
        }
        UnaryNode.prototype.compute = function () {
            var v = this.node.compute();
            switch (this.op) {
                case "-": return -v;
                case "NOT": return !v;
            }
            throw new Error("operator not implemented '" + this.op + "'");
        };
        UnaryNode.prototype.toString = function (parseVariables) {
            if (parseVariables === void 0) { parseVariables = false; }
            var op = BinaryNode.isKeyword(this.op) ? this.op + " " : this.op;
            return "(" + op + this.node.toString(parseVariables) + ")";
        };
        return UnaryNode;
    }(GraphNode));
    var ExpressionStringNode = /** @class */ (function (_super) {
        __extends(ExpressionStringNode, _super);
        function ExpressionStringNode(expression) {
            var _this = _super.call(this) || this;
            _this.expression = expression;
            _this.innerTree = parse(expression);
            return _this;
        }
        ExpressionStringNode.prototype.compute = function () {
            if (this.innerTree)
                return this.innerTree.compute();
            return "";
        };
        ExpressionStringNode.prototype.toString = function (parseVariables) {
            if (parseVariables === void 0) { parseVariables = false; }
            if (this.innerTree)
                return "'" + this.innerTree.toString(parseVariables) + "'";
            return "''";
        };
        return ExpressionStringNode;
    }(GraphNode));
    function parse(str) {
        function extractTokens(exp) {
            //dynamically build my parsing regex:
            var tokenParser = new RegExp([
                //properties
                /\[[a-zA-Z0-9$_]*\]+/.source,
                //numbers
                /\d+(?:\.\d*)?|\.\d+/.source,
                //string-literal
                /"(?:\\[\s\S]|[^"])*"/.source,
                /'(?:\\[\s\S]|[^'])*'/.source,
                //booleans (bounded so an identifier such as trueUp is not split into true + Up)
                /\b(?:true|false)\b/.source,
                //operators: keywords first (bounded, so ORDER_Total / Andrew / NotifyFlag stay
                //identifiers), then the symbols
                "\\b(?:" + Object.keys(BinaryNode.keywords).join("|") + ")\\b|" +
                ["(", ")"].concat(BinaryNode.operators)
                    .map(function (str) { return String(str).replace(/[.*+?^=!:${}()|[\]\/\\]/g, '\\$&'); })
                    .join("|"),
                //properties
                //has to be after the operators
                /[a-zA-Z$_][a-zA-Z0-9$_]*/.source,
                //remaining (non-whitespace-)chars, just in case
                //has to be at the end
                /\S/.source
            ].map(function (s) { return "(" + s + ")"; }).join("|"), "gi"); // i: keywords and true/false in any casing, as in Flee
            var _tokens = [];
            //abusing str.replace() as a RegExp.forEach
            exp.replace(tokenParser, function (token, prop, number, dblStr, sglStr, boolean, op, property) {
                var t;
                t = token;
                if (number)
                    t = new ValueNode(+number);
                else if (dblStr) {
                    var str = dblStr.substr(1, dblStr.length - 2);
                    str = str.replace(/\\"/g, '"');
                    t = new ValueNode(str);
                }
                else if (sglStr) {
                    var innerExpression = sglStr.substr(1, sglStr.length - 2);
                    innerExpression = innerExpression.replace(/\\'/g, "'");
                    t = new ExpressionStringNode(innerExpression);
                }
                else if (boolean)
                    t = new ValueNode(boolean.toLowerCase() === 'true');
                else if (property)
                    t = new PropertyNode(property);
                else if (prop)
                    t = new PropertyNode(prop.substring(1, prop.length - 1), true);
                else if (token == ',')
                    t = new CommaNode();
                else if (op)
                    t = BinaryNode.keywords[op.toUpperCase()] || op;
                else
                    throw new Error("unexpected token '" + token + "'");
                _tokens.push(t);
                return "";
            });
            return _tokens;
        }
        function wrapParenteses(tokens) {
            // An operand is any node except an argument separator. By the time process() runs,
            // parenthesised groups and function calls are already nodes, so the only raw
            // strings left in the token list are operators.
            function isOperand(t) {
                return t instanceof GraphNode && !(t instanceof CommaNode);
            }
            function isPrefixPosition(tokens, i) {
                return i === 0 || typeof tokens[i - 1] === 'string' || tokens[i - 1] instanceof CommaNode;
            }
            // Prefix operators reduce right to left so "- -x" nests correctly. A literal number
            // folds into a negative ValueNode (so "(-1)" still saves as "-1", as it always has).
            function reducePrefix(tokens, op) {
                for (var i = tokens.length - 2; i >= 0; i--) {
                    if (tokens[i] === op && isPrefixPosition(tokens, i)) {
                        var operand = tokens[i + 1];
                        if (!isOperand(operand))
                            throw new Error("Please enter a valid expression");
                        var node = (op === "-" && operand instanceof ValueNode && typeof operand.value === 'number')
                            ? new ValueNode(-operand.value)
                            : new UnaryNode(op, operand);
                        tokens.splice(i, 2, node);
                    }
                }
            }
            // Binary operators of one precedence tier reduce left to right.
            function reduceBinaryTier(tokens, ops) {
                for (var i = 0; i < tokens.length; i++) {
                    if (typeof tokens[i] === 'string' && ops.indexOf(tokens[i]) > -1) {
                        if (!isOperand(tokens[i - 1]) || !isOperand(tokens[i + 1]))
                            throw new Error("Please enter a valid expression");
                        tokens.splice(i - 1, 3, new BinaryNode(tokens[i], tokens[i - 1], tokens[i + 1]));
                        i--;
                    }
                }
            }
            function process(tokens) {
                BinaryNode.tiers.forEach(function (tier) {
                    if (tier.prefix)
                        reducePrefix(tokens, tier.prefix);
                    else
                        reduceBinaryTier(tokens, tier.binary);
                });
                var hasComma = false;
                for (var j = 0; j < tokens.length; j++) {
                    if (tokens[j] instanceof CommaNode) {
                        hasComma = true;
                        break;
                    }
                }
                if (hasComma) {
                    var commaCount = tokens.filter(function (t) { return t == ","; }).length, argCount = commaCount * 2 + 1;
                    if (tokens.length != argCount)
                        throw new Error("Syntax error for the arguments: " + tokens.filter(function (t) { return !(t instanceof CommaNode); }).join(","));
                    tokens = [new ValueNode(tokens)];
                }
                if (tokens.length !== 1) {
                    console.log("error: ", tokens.slice());
                    throw new Error("Please enter a valid expression");
                }
                return tokens[0];
            }
            //wrap inside any parentheses
            for (var i, j; (i = tokens.lastIndexOf("(")) > -1 && (j = tokens.indexOf(")", i)) > -1;) {
                //if before parentheses there is a property which means it is a function
                if (tokens[i - 1] instanceof PropertyNode && !tokens[i - 1].inBrackets) {
                    var op = tokens[i - 1].toString();
                    var funcParam = i + 1 == j ? new ValueNode([]) : process(tokens.slice(i + 1, j));
                    var varsLength = 1;
                    if (funcParam instanceof ValueNode && funcParam.value instanceof Array)
                        varsLength = funcParam.value.filter(function (v) { return !(v instanceof CommaNode); }).length; //remove            
                    var func = options.funcs[op];
                    if (!func) {
                        throw new Error(op + " is not defined.");
                    }
                    var minArgs = typeof func.minArgs === "number" ? func.minArgs : func.length;
                    var maxArgs = typeof func.maxArgs === "number" ? func.maxArgs : func.length;
                    if (varsLength < minArgs || varsLength > maxArgs) {
                        var argsText = minArgs === maxArgs ? minArgs + "" : (minArgs + "-" + maxArgs);
                        throw new Error(op + " requires " + argsText + " argument(s)");
                    }
                    var funcNode = new FuncNode(op, funcParam);
                    tokens.splice(i - 1, j + 2 - i, funcNode);
                }
                else
                    tokens.splice(i, j + 1 - i, process(tokens.slice(i + 1, j)));
            }
            if (~tokens.indexOf("(") || ~tokens.indexOf(")")) {
                throw new Error("mismatching brackets");
            }
            return process(tokens);
        }
        return wrapParenteses(extractTokens(str));
    }
    return {
        getExpressionTree: function () {
            try {
                return parse(expression);
            }
            catch (e) {
                return undefined;
            }
        },
        runExpressionTree: function () {
            try {
                var tree = parse(expression);
                return tree.compute();
            }
            catch (e) {
                return undefined;
            }
        },
        validate: function () {
            var result = "";
            try {
                parse(expression).compute();
            }
            catch (e) {
                result = e.message;
            }
            return result;
        }
    };
}

/**
 * Test/diagnostic helper: the editor's verdict for one expression. Either the
 * validation message or the canonical text the editor would save.
 */
export function canonicalize(expression, options) {
    var p = createExpressionParser(expression, options);
    var message = p.validate();
    if (message) return { ok: false, message: message };
    var tree = p.getExpressionTree();
    if (!tree) return { ok: false, message: 'Please enter a valid expression' };
    return { ok: true, canonical: tree.toString(true) };
}
